import upperFirst from 'lodash/upperFirst';
import React, { CSSProperties, useEffect, useState } from 'react';
import { ErrorMessage, Field, FieldProps } from '@atlaskit/form';
import { RadioGroup } from '@atlaskit/radio';
import Select, { ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { Maybe } from '@css-xp/graphtypes';
import { TasstaxIdLabel, TaxId } from '@css-xp/graphtypes/src/generated-types-new';
import { DISPLAY_NAME_KEY, TAX_ID_KEY, TAX_LABEL_STRING, VAT_STATUS_KEY } from '@atlassian/cssxp-types-billing';
import { ENABLE_MULTIPLE_TAX_IDS, buildFieldName } from '@atlassian/cssxp-utils-generic';
import '../../assets/css/billing-shipping-forms.scss';
import { useCountryService, useCountryStateService, useFeatureToggle } from '../../hooks';
import { getDefaultTaxId, getDefaultVatStatus } from '../../tax-exempt';

interface FieldErrors {
  [key: string]: string;
}
interface Props {
  name?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postcode?: string | null;
  taxId?: string | null;
  taxIds?: Maybe<Maybe<TaxId>[]>;
  addressName?: 'Bill to' | 'Ship to' | 'Sold to';
  isEditable?: boolean | false;
  isRequired?: boolean;
  // when in a form, this allows the form to manipulate based on other selections
  setFieldValue?: (name: string, value: unknown) => void;
  hideAddressNameLabel?: boolean;
  // to append to GST PST label
  taxIdAddressPrefix?: 'Sold-to';
  isChecked?: boolean;
  fieldErrors?: FieldErrors;
}

// TODO: There should be a test associated with this file.

/**
 * This component combines several address fields together so the fields and
 * layout can be reused in multiple forms.
 */
export const AddressFormSection = ({
  name = null,
  addressLine1 = null,
  addressLine2 = null,
  city = null,
  state = null,
  country = null,
  postcode = null,
  addressName,
  taxId = null,
  taxIds = [],
  isEditable,
  setFieldValue,
  isRequired = true,
  hideAddressNameLabel,
  taxIdAddressPrefix,
  isChecked,
  fieldErrors,
}: Props) => {
  const customBuildFieldName = buildFieldName.bind(this, addressName ?? '');

  const countryIdentifier = customBuildFieldName('country');
  const stateIdentifier = customBuildFieldName('state');
  const [isCountryModified, setIsCountryModified] = useState(false);
  const [defaultStateValue, setDefaultStateValue] = useState<IsoSelectItem>();
  const [defaultCountryValue, setDefaultCountryValue] = useState<IsoSelectItem>();

  // TODO refactor the country field out of address form section
  const { countries, isLoadingCountries } = useCountryService();
  const [availableCountriesItems, setAvailableCountriesItems] = useState<IsoSelectItem[]>([]);

  // TODO refactor the state field out of address form section
  const { states, isLoadingStates, fetchStatesForCountry, taxLabels } = useCountryStateService();

  const [availableStatesItems, setAvailableStatesItems] = useState<IsoSelectItem[]>([]);
  const [taxIdLabels, setTaxIdLabels] = useState<Maybe<TasstaxIdLabel>[]>([]);
  const [isEnabled] = useFeatureToggle();
  const isAlternateTaxationEnabled = isEnabled(ENABLE_MULTIPLE_TAX_IDS);

  //To unset the values for country and state after checkbox is unchecked
  useEffect(() => {
    if (isChecked !== null && isChecked !== undefined && !isChecked) {
      setDefaultCountryValue({ label: '', value: '' });
      setDefaultStateValue({ label: '', value: '' });
    }
  }, [isChecked]);

  useEffect(() => {
    const availableCountries = (countries?.nodes ?? []).map(
      (mappedCountry) =>
        ({
          label: mappedCountry?.name,
          value: mappedCountry?.isoCode,
        } as IsoSelectItem)
    );

    setAvailableCountriesItems(availableCountries);
  }, [countries?.nodes]);

  useEffect(() => {
    if (country) {
      const defaultCountry = (availableCountriesItems ?? []).find((mappedCountry) => country === mappedCountry?.value);
      fetchStatesForCountry(country);
      setFieldValue?.(countryIdentifier, defaultCountry);
      setDefaultCountryValue(defaultCountry);
    }
  }, [availableCountriesItems, country, fetchStatesForCountry, countryIdentifier, setFieldValue]);

  useEffect(() => {
    const availableStates = (states?.nodes ?? []).map(
      (mappedState) =>
        ({
          label: mappedState?.name,
          value: mappedState?.isoCode,
        } as IsoSelectItem)
    );
    setAvailableStatesItems(availableStates);
  }, [states?.nodes]);

  useEffect(() => {
    if (state && !isCountryModified) {
      const defaultState = (availableStatesItems ?? []).find(
        (mappedState) => state === mappedState?.value || state?.toLowerCase() === mappedState?.label?.toLowerCase()
      );
      setFieldValue?.(stateIdentifier, defaultState);
      setDefaultStateValue(defaultState);
      handleAlternateTaxationForState(defaultState);
    }
  }, [availableStatesItems, state, setFieldValue, stateIdentifier, isCountryModified]);

  const taxIdLabel = 'Tax ID';

  const handleAlternateTaxationForState = async (state?: IsoSelectItem) => {
    if (taxLabels && taxLabels?.length > 0) {
      let taxLabel = taxLabels.filter((states) => states?.region === state?.value);
      if (taxLabel.length === 0) {
        taxLabel = taxLabels.filter((states) => states?.region === 'XX');
      }
      //Need to take care as data not coming properly(i.e> giving type and displayname as null from TAAS)
      if (taxLabel?.[0]?.taxIdLabels?.[0]?.type && taxLabel?.[0]?.taxIdLabels?.[0]?.displayName) {
        setTaxIdLabels(taxLabel?.[0]?.taxIdLabels);
      } else {
        setTaxIdLabels([]);
      }
    } else {
      setTaxIdLabels([]);
    }
  };

  const onCountryChange = async (selectedItem?: IsoSelectItem) => {
    setIsCountryModified(true);
    setAvailableStatesItems([]);

    // Once you change, no more default state item
    setFieldValue?.(stateIdentifier, undefined);

    if (selectedItem) {
      await fetchStatesForCountry(selectedItem.value);
    }
  };

  const onStateChange = (selectedItem?: IsoSelectItem) => {
    handleAlternateTaxationForState(selectedItem);
  };

  return (
    <>
      <AddressTextField
        label={hideAddressNameLabel ? 'Company name' : `${addressName} company name`}
        name={customBuildFieldName('name')}
        defaultValue={name}
        isReadOnly={!isEditable}
        isRequired={isRequired}
        validationError={fieldErrors?.['name']}
      />
      <div>
        <AddressTextField
          label="Address 1"
          name={customBuildFieldName('address1')}
          defaultValue={addressLine1}
          isReadOnly={!isEditable}
          isRequired={isRequired}
          validationError={fieldErrors?.['address1']}
        />
      </div>
      <AddressTextField
        label="Address 2"
        name={customBuildFieldName('address2')}
        defaultValue={addressLine2}
        isReadOnly={!isEditable}
        validationError={fieldErrors?.['address2']}
      />
      <CountrySelectField
        label="Country"
        name={countryIdentifier}
        isReadOnly={!isEditable}
        options={availableCountriesItems}
        isLoading={isLoadingCountries}
        isRequired={isRequired}
        defaultValue={defaultCountryValue}
        onCountryChange={onCountryChange}
      />
      <StateSelectField
        label="State"
        name={stateIdentifier}
        isReadOnly={!isEditable}
        options={availableStatesItems}
        isLoading={isLoadingStates}
        defaultValue={defaultStateValue}
        isRequired={isRequired}
        onStateChange={onStateChange}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
        }}
      >
        <AddressTextField
          label="City"
          name={customBuildFieldName('city')}
          defaultValue={city}
          isReadOnly={!isEditable}
          style={{ marginRight: '8px', flex: 1 }}
          isRequired={isRequired}
          validationError={fieldErrors?.['city']}
        />
        <AddressTextField
          label="Postal code"
          name={customBuildFieldName('postalCode')}
          defaultValue={postcode}
          isReadOnly={!isEditable}
          style={{ marginRight: '8px', flex: 0.5 }}
          isRequired={isRequired}
          validationError={fieldErrors?.['postalCode']}
        />
      </div>

      {!isAlternateTaxationEnabled || taxIdLabels.length === 0 ? (
        <AddressTextField
          label={hideAddressNameLabel ? 'Tax ID' : taxIdLabel}
          name={customBuildFieldName('taxId')}
          defaultValue={taxId}
          isReadOnly={!isEditable}
          validationError={fieldErrors?.['taxId']}
        />
      ) : (
        <>
          {taxIdLabels?.map((tax) => (
            <TaxLabelField
              taxInfo={tax}
              key={`tax_label_${tax?.type}`}
              customBuildFieldName={customBuildFieldName}
              taxIds={taxIds}
              isEditable={isEditable}
              taxIdAddressPrefix={taxIdAddressPrefix}
              error={fieldErrors?.[`taxId_${tax?.type}`]}
            />
          ))}
        </>
      )}
    </>
  );
};

interface AddressTextFieldProps {
  label: string;
  name: string;
  defaultValue?: string | null;
  isReadOnly: boolean;
  style?: CSSProperties;
  isRequired?: boolean | false;
  onValueChange?: (event: React.FormEvent<HTMLInputElement>) => void;
  validationError?: string;
}

const AddressTextField = ({
  label,
  name,
  defaultValue,
  isReadOnly,
  style = {},
  isRequired = false,
  onValueChange = () => undefined,
  validationError,
}: AddressTextFieldProps) => {
  return (
    <div style={style}>
      <Field
        label={label}
        name={name}
        defaultValue={defaultValue || ''}
        isDisabled={isReadOnly}
        isRequired={isRequired}
        validate={(value) => {
          if (!value && isRequired) {
            return 'Required';
          }
          if (value && value.length > 50) {
            if (label === 'Address 1') return 'You have exceeded the character limit for this line. Use the next line.';
            else if (label === 'City') return 'You have exceeded the character limit. Check for typos and try again';
          }
          if (value && value.length > 100 && label === 'Address 2') {
            return 'You have exceeded the character limit. Remove any extra information.';
          }
          if (value && label === 'Postal code' && value.length > 11) {
            return 'The postal code seems incorrect. Check the postal code of your area and try again.';
          }
          return undefined;
        }}
      >
        {({ fieldProps, error }: { fieldProps: FieldProps<string, HTMLInputElement>; error?: string }) => (
          <>
            <Textfield
              {...fieldProps}
              autoComplete="off"
              onChange={(event) => {
                onValueChange(event);
                fieldProps.onChange(event);
              }}
              testId="address-field"
              isInvalid={!!validationError}
            />
            {(error || validationError) && <ErrorMessage>{error || validationError}</ErrorMessage>}
          </>
        )}
      </Field>
    </div>
  );
};

export interface IsoSelectItem {
  label: string; // countryName
  value: string; // isoCode
}

interface CountrySelectFieldProps {
  label: string;
  name: string;
  value?: IsoSelectItem;
  isReadOnly: boolean;
  style?: CSSProperties;
  isRequired?: boolean | false;
  options: IsoSelectItem[];
  isLoading: boolean;
  onCountryChange: (selectedItem?: IsoSelectItem) => void;
  defaultValue?: IsoSelectItem | null;
}

const CountrySelectField = ({
  label,
  name,
  isReadOnly,
  style = {},
  isRequired,
  options,
  isLoading,
  defaultValue,
  onCountryChange = () => {
    return;
  },
}: CountrySelectFieldProps) => {
  return (
    <div style={style}>
      <Field<ValueType<IsoSelectItem>, HTMLSelectElement>
        label={label}
        name={name}
        isDisabled={isReadOnly}
        isRequired={isRequired}
        defaultValue={defaultValue}
      >
        {({ fieldProps }) => (
          <>
            <Select<IsoSelectItem>
              {...fieldProps}
              options={options}
              placeholder="Select country or type the first letter"
              testId="country-address-field"
              isLoading={isLoading}
              //@ts-ignore -- Complaining about onChange
              onChange={(selectedItem: IsoSelectItem) => {
                onCountryChange(selectedItem);
                fieldProps.onChange(selectedItem);
              }}
            />
          </>
        )}
      </Field>
    </div>
  );
};

interface StateSelectFieldProps {
  label: string;
  name: string;
  isReadOnly: boolean;
  style?: CSSProperties;
  isRequired?: boolean | false;
  isLoading: boolean;
  options: IsoSelectItem[];
  onStateChange?: (selectedItem?: IsoSelectItem) => void;
  defaultValue?: IsoSelectItem | null;
}

export const StateSelectField = ({
  label,
  name,
  isReadOnly,
  style = {},
  isRequired,
  isLoading,
  options,
  defaultValue,
  onStateChange = () => {
    return;
  },
}: StateSelectFieldProps) => {
  return (
    <div style={style}>
      <Field<ValueType<IsoSelectItem>, HTMLSelectElement>
        label={label}
        name={name}
        isDisabled={isReadOnly || options.length === 0}
        isRequired={isRequired && options.length !== 0}
        defaultValue={defaultValue}
      >
        {({ fieldProps }) => (
          <>
            <Select<IsoSelectItem>
              {...fieldProps}
              key={`${name}-select-${fieldProps.value}`}
              options={options.sort((a, b) => a.label?.localeCompare(b.label))}
              placeholder="Select state or type the first letter"
              testId="state-address-field"
              isLoading={isLoading}
              //@ts-ignore -- Complaining about onChange
              onChange={(selectedItem: IsoSelectItem) => {
                onStateChange(selectedItem);
                fieldProps.onChange(selectedItem);
              }}
            />
          </>
        )}
      </Field>
    </div>
  );
};

interface TaxLabelFieldProps {
  taxInfo: Maybe<TasstaxIdLabel>;
  taxIdAddressPrefix?: string;
  isEditable?: boolean;
  taxIds: Maybe<Maybe<TaxId>[]>;
  customBuildFieldName: (name: string) => void;
  error?: string;
}

export const TaxLabelField = ({
  taxInfo,
  taxIdAddressPrefix,
  isEditable,
  taxIds,
  customBuildFieldName,
  error,
}: TaxLabelFieldProps) => {
  const defaultTaxId = getDefaultTaxId(taxIds, taxInfo?.type ?? '');
  const [isEmpty, setIsEmpty] = useState(!defaultTaxId);

  const handleTaxIdChange = (taxId?: string | null) => {
    setIsEmpty(taxId ? false : true);
  };

  const onTaxIdChange = (event: React.FormEvent<HTMLInputElement>) => {
    handleTaxIdChange(event.currentTarget.value);
  };

  useEffect(() => {
    handleTaxIdChange(defaultTaxId);
  }, [defaultTaxId]);

  return (
    <>
      <AddressTextField
        key={taxInfo?.type}
        label={`${
          taxIdAddressPrefix ? `${taxIdAddressPrefix} ${taxInfo?.displayName}` : `${taxInfo?.displayName}`
        } Tax ID`}
        name={`${customBuildFieldName(TAX_LABEL_STRING)}__${taxInfo?.type}__${TAX_ID_KEY}`}
        defaultValue={defaultTaxId}
        isReadOnly={!isEditable}
        onValueChange={onTaxIdChange}
        validationError={error}
      />
      <Field
        name={`${customBuildFieldName(TAX_LABEL_STRING)}__${taxInfo?.type}__${DISPLAY_NAME_KEY}`}
        defaultValue={taxInfo?.displayName}
      >
        {() => null}
      </Field>
      {!isEmpty && taxInfo?.metadata?.labels?.length ? (
        <Field
          name={`${customBuildFieldName(TAX_LABEL_STRING)}__${taxInfo.type}__${VAT_STATUS_KEY}`}
          isRequired
          defaultValue={getDefaultVatStatus(taxIds, taxInfo.metadata.labels, taxInfo?.type ?? '')}
        >
          {({ fieldProps }) => (
            <RadioGroup
              {...fieldProps}
              options={(taxInfo?.metadata?.labels || []).map((each) => ({
                value: each?.vatStatus || '',
                label: upperFirst(each?.label || ''),
              }))}
            />
          )}
        </Field>
      ) : null}
    </>
  );
};
